import React, {useState, useEffect, useCallback, useRef} from 'react'
import {useNavigate} from 'react-router-dom'
import {KTSVG} from '../../../_metronic/helpers'
import axios from 'axios'
import toaster from '../../../Utils/toaster'
import {CommonPaginationModel} from '../../../Utils/commonPagination'
import { TranscriptionInfoCard } from './components/TranscriptionInfoCard'
import {useAuth} from '../../modules/auth'
import {UsersListLoading} from '../../UI/customers-management/users-list/components/loading/UsersListLoading'

interface TranscriptionRequest {
  id: number
  requesterId: string
  requesterName?: string
  serviceType: string
  audioDuration: string | number
  dueDate: string
  assigneeId?: string
  assigneeName?: string
  reviewerId?: string
  reviewerName?: string
  status: string
  reviewRequired: boolean
  callId: string | null
  audioFileUrl: string | null
  clientInstruction: string | null
  createdAt: string
  updatedAt: string
  createdBy: string
  createdByName?: string
  updatedBy: string
  updatedByName?: string
  companyName?: string
  formattedAudioDuration?: string
  formattedDueDate?: string
}

interface TranscriptionSummary {
  total: number
  pending: number
  inProgress: number
  completed: number
}

export function TranscriptionsView() {
  const navigate = useNavigate()
  const {currentUser} = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [serviceTypeFilter, setServiceTypeFilter] = useState('All Service Types')
  const [statusFilter, setStatusFilter] = useState('All Statuses')
  const [currentPage, setCurrentPage] = useState(1)
  const [transcriptionRequests, setTranscriptionRequests] = useState<TranscriptionRequest[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [error, setError] = useState<string>('')
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })
  const defaultRowsPerPage =
    Number(process.env.REACT_APP_PAGINATION_ITEMS_PER_PAGE) > 0
      ? Number(process.env.REACT_APP_PAGINATION_ITEMS_PER_PAGE)
      : 10
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage)

  const [totalCount, setTotalCount] = useState(0)
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [summary, setSummary] = useState<TranscriptionSummary>({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
  })

  const API_URL = process.env.REACT_APP_API_URL

  const statusCategoryParam = (status: string): string | undefined => {
    if (status === 'All Statuses') return undefined
    if (status === 'Pending') return 'Pending'
    if (status === 'In Progress') return 'InProgress'
    if (status === 'Completed') return 'Completed'
    return undefined
  }

  const lastListFilterKeyRef = useRef<string | null>(null)

  const fetchSummary = useCallback(async () => {
    try {
      const response = await axios.get<TranscriptionSummary>(`${API_URL}/Transcription/summary-cards`)
      setSummary({
        total: response.data?.total ?? 0,
        pending: response.data?.pending ?? 0,
        inProgress: response.data?.inProgress ?? 0,
        completed: response.data?.completed ?? 0,
      })
    } catch {
      setSummary({ total: 0, pending: 0, inProgress: 0, completed: 0 })
    }
  }, [API_URL])

  const fetchTranscriptions = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const filterKey = `${debouncedSearch}|${serviceTypeFilter}|${statusFilter}`
      let pageToRequest = currentPage
      if (lastListFilterKeyRef.current !== filterKey) {
        lastListFilterKeyRef.current = filterKey
        pageToRequest = 1
        if (currentPage !== 1) {
          setCurrentPage(1)
        }
      }

      const statusCategory = statusCategoryParam(statusFilter)

      const response = await axios.get(`${API_URL}/Transcription`, {
        params: {
          page: pageToRequest,
          pageSize: rowsPerPage,
          ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
          ...(serviceTypeFilter !== 'All Service Types' ? { serviceType: serviceTypeFilter } : {}),
          ...(statusCategory ? { statusCategory } : {}),
        },
      })

      const list = response.data?.transcriptions
      if (Array.isArray(list)) {
        setTranscriptionRequests(list)
        const tc = response.data?.totalCount
        setTotalCount(typeof tc === 'number' ? tc : list.length)
      } else if (Array.isArray(response.data)) {
        setTranscriptionRequests(response.data)
        setTotalCount(response.data.length)
      } else {
        setTranscriptionRequests([])
        setTotalCount(0)
      }
    } catch {
      setError('Failed to fetch transcriptions')
      setTranscriptionRequests([])
      setTotalCount(0)
      toaster('error', 'Failed to load transcriptions')
    } finally {
      setIsLoading(false)
      setIsInitialLoading(false)
    }
  }, [
    API_URL,
    currentPage,
    debouncedSearch,
    rowsPerPage,
    serviceTypeFilter,
    statusFilter,
  ])

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchTerm), 400)
    return () => window.clearTimeout(t)
  }, [searchTerm])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  useEffect(() => {
    fetchTranscriptions()
  }, [fetchTranscriptions])

  const totalPages = totalCount === 0 ? 1 : Math.ceil(totalCount / rowsPerPage)

  const handleViewRequest = (request: TranscriptionRequest) => {
    navigate(`/transcriptions/${request.id}`)
  }

  const handleCreateNewRequest = () => {
    // toaster('success', 'Redirecting to create new transcription request...')
    navigate('/transcriptions/create')
  }

  const handleDropdownToggle = (requestId: number, event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    
    if (activeDropdown === requestId) {
      setActiveDropdown(null)
    } else {
      const button = event.currentTarget as HTMLElement
      const rect = button.getBoundingClientRect()
      
      setDropdownPosition({
        top: rect.bottom + 5,
        left: rect.left - 120 // Position dropdown to the left of button
      })
      setActiveDropdown(requestId)
    }
  }

  const handleClickOutside = () => {
    setActiveDropdown(null)
  }

  // Add click outside listener
  useEffect(() => {
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Complete':
        return <span className='badge' style={{backgroundColor: '#e8f5e8', color: '#2e7d32', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500'}}>Completed</span>
    
      case 'Ready To Assign':
        return <span className='badge' style={{backgroundColor: '#fff3cd', color: '#856404', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500'}}>Pending</span>
      case 'ReadyToAssign':
        return <span className='badge' style={{backgroundColor: '#fff3cd', color: '#856404', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500'}}>Pending</span>
      case 'Transcription Complete':
        return <span className='badge' style={{backgroundColor: '#e3f2fd', color: '#7b1fa2', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500'}}>In Progress</span>
      case 'In Progress':
        return <span className='badge' style={{backgroundColor: '#e3f2fd', color: '#7b1fa2', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500'}}>In Progress</span>
      case 'In Review':
        return <span className='badge' style={{backgroundColor: '#fff3cd', color: '#856404', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500'}}>In Review</span>
      case 'Pending':
        return <span className='badge' style={{backgroundColor: '#fff3cd', color: '#856404', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500'}}>Pending</span>
      case 'Assigned':
        return <span className='badge' style={{backgroundColor: '#e3f2fd', color: '#7b1fa2', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500'}}>In Progress</span>
      default:
        return <span className='badge' style={{backgroundColor: '#f5f5f5', color: '#6c757d', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500'}}>{status}</span>
    }
  }

  const getReviewBadge = (reviewRequired: boolean) => {
    return reviewRequired ? 
      <span className='badge' style={{backgroundColor: '#e3f2fd', color: '#1976d2', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500'}}>Required</span> : 
      <span className='badge' style={{backgroundColor: '#fff3cd', color: '#856404', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500'}}>Not Required</span>
  }

  // if (isLoading) {
  //   return (
  //     <div className='d-flex justify-content-center align-items-center' style={{height: '400px'}}>
  //       <div className='spinner-border text-primary' role='status'>
  //         <span className='visually-hidden'>Loading...</span>
  //       </div>
  //     </div>
  //   )
  // }

  // if (error) {
  //   return (
  //     <div className='alert alert-danger d-flex align-items-center p-5 mb-5'>
  //       <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-2hx svg-icon-danger me-4' />
  //       <div className='d-flex flex-column'>
  //         <h4 className='mb-1'>Error</h4>
  //         <span>{error}</span>
  //       </div>
  //     </div>
  //   )
  // }

  if (isInitialLoading) {
    return <UsersListLoading />
  }

  return (
    <>
      {/* Page Header */}
      <div className='mb-3 d-flex flex-column'>
        <div className='d-flex'>
          <h1 className='mb-0 fw-bold mb-2' style={{fontSize: '25px', lineHeight: '32px'}}>
            Transcriptions
          </h1>
        </div>
        <div className='d-flex'>
          <p className='text-gray-500 fs-5 mb-2'>Manage Transcription Requests</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className='row g-5 g-xl-8 mb-5'>
        <TranscriptionInfoCard title='Total Requests' total={summary.total} path='/media/icons/duotune/files/fil003.svg'/>
        <TranscriptionInfoCard title='Pending Requests' total={summary.pending} path='/media/icons/duotune/general/gen013.svg'/>
        <TranscriptionInfoCard title='In Progress' total={summary.inProgress} path='/media/icons/duotune/general/gen012.svg'/>
        <TranscriptionInfoCard title='Completed Requests' total={summary.completed} path='/media/icons/duotune/arrows/arr016.svg'/>
      </div>

      {/* Requests Table */}
      <div className='card'>
        <div className='card-body py-3'>
          {/* Search and Filters */}
          <div className='d-flex justify-content-between align-items-center mb-5 flex-wrap'>
            <div className='d-flex align-items-center gap-3 mt-2 flex-wrap'>
              <div className='d-flex align-items-center position-relative'>
                <KTSVG
                  path='/media/icons/duotune/general/gen021.svg'
                  className='svg-icon-1 position-absolute ms-6'
                />
                <input
                  type='text'
                  className='form-control w-250px ps-14'
                  placeholder='Search requests...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <select
                className='form-select w-200px'
                value={serviceTypeFilter}
                onChange={(e) => setServiceTypeFilter(e.target.value)}
              >
                <option value='All Service Types'>All Service Types</option>
                <option value='Transcription'>Transcription</option>
                {/* <option value='Translation'>Translation</option> */}
              </select>

              <select
                className='form-select w-200px'
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value='All Statuses'>All Statuses</option>
                <option value='Pending'>Pending</option>
                <option value='In Progress'>In Progress</option>
                <option value='Completed'>Completed</option>
              </select>
            </div>

            {currentUser?.result?.userType !== 'INTERPRETER' && (
              <div className='mt-2 ml-2'>
                <button
                  type='button'
                  className='btn btn-danger'
                  onClick={handleCreateNewRequest}
                >
                  + New Request
                </button>
              </div>
            )}
          </div>

          {/* Table */}
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th className='min-w-150px'>Company</th>
                  <th className='min-w-140px'>Requester</th>
                  <th className='min-w-120px'>Service Type</th>
                  <th className='min-w-120px'>Review</th>
                  <th className='min-w-120px'>Assignee</th>
                  <th className='min-w-120px'>Reviewer</th>
                  <th className='min-w-100px'>Status</th>
                  <th className='min-w-100px'>Due Date</th>
                  {/* <th className='min-w-100px text-end'>Actions</th> */}
                </tr>
              </thead>
              <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className='text-center'>
                    
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className='text-center'>
                    <div className='text-danger'>{error}</div>
                  </td>
                </tr>
              ) : transcriptionRequests.length === 0 ? (
                <tr>
                  <td colSpan={8} className='text-center py-10'>
                    <div className='d-flex flex-column align-items-center'>
                      <KTSVG
                        path='/media/icons/duotune/files/fil003.svg'
                        className='svg-icon-3x svg-icon-muted mb-3'
                      />
                      <div className='text-muted fs-5 fw-semibold'>No transcriptions available</div>
                      <div className='text-muted fs-7'>There are no transcription requests to display</div>
                    </div>
                  </td>
                </tr>
              ) : (
              transcriptionRequests.map((request) => (
                  <tr key={request.id}>
                    <td>
                      <div className='d-flex align-items-center'>
                        <div className='d-flex justify-content-start flex-column'>
                          <a href={`/transcriptions/${request.id}`} className='text-dark fw-bold text-hover-primary fs-6'>
                            {request.companyName}
                          </a>
                          <small className='text-muted'>ID: {request.id}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span  className='text-gray-500 text-hover-primary d-block fs-6'>
                        {request.requesterName}
                      </span>
                    </td>
                    <td>
                      <span className='text-gray-500 d-block fs-7'>
                        {request.serviceType}
                      </span> 
                    </td>
                    <td>
                      {getReviewBadge(request.reviewRequired)}
                    </td>
                    <td>
                      <span className='text-gray-500 text-hover-primary d-block fs-6'>
                        {request.assigneeName || 'Not Assigned'}
                      </span>
                    </td>
                    <td>
                      <span className='text-gray-500 text-hover-primary d-block fs-6'>
                        {request.reviewRequired === false  ? "Not Required":  request.reviewerName }
                      </span>
                    </td>
                    <td>{getStatusBadge(request.status)}</td>
                    <td>
                      <span className='text-muted fw-semibold text-muted d-block fs-7'>
                        {request.formattedDueDate}
                      </span>
                    </td>
                    {/* <td>
                      <div className='d-flex justify-content-end flex-shrink-0'>
                        <div className='dropdown position-relative'>
                          <button
                            type='button'
                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                            onClick={(e) => handleDropdownToggle(request.id, e)}
                          >
                            <KTSVG path='/media/icons/duotune/general/gen019.svg' className='svg-icon-3' />
                          </button>
                        </div>
                      </div>
                    </td> */}
                  </tr>
                ))
              )}
              </tbody>
            </table>
          </div>

          {/* Custom Dropdown Portal */}
          {/* {activeDropdown && (
            <div 
              style={{
                position: 'fixed',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                zIndex: 9999,
                minWidth: '150px',
                backgroundColor: '#fff',
                border: '1px solid #e4e6ef',
                borderRadius: '0.475rem',
                boxShadow: '0 0.5rem 1.5rem 0.5rem rgba(0, 0, 0, 0.075)',
                padding: '0.5rem 0'
              }}
            >
              <div 
                className='dropdown-item d-flex align-items-center px-3 py-2'
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  const request = transcriptionRequests.find(r => r.id === activeDropdown)
                  if (request) {
                    handleViewRequest(request)
                  }
                  setActiveDropdown(null)
                }}
                style={{
                  color: '#181c32',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3 me-2' />
                View
              </div>
              <div 
                className='dropdown-item d-flex align-items-center px-3 py-2'
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setActiveDropdown(null)
                }}
                style={{
                  color: '#181c32',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3 me-2' />
                Edit
              </div>
            </div>
          )} */}

          <CommonPaginationModel
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            total={totalCount}
            onPageChange={(page: number) => setCurrentPage(page)}
            onRowsPerPageChange={(newRowsPerPage: number) => {
              setRowsPerPage(newRowsPerPage)
              setCurrentPage(1)
            }}
          />
        </div>
      </div>
    </>
  )
} 